import type { z } from 'zod';
import type { ModuleId } from './features.ts';
import type { AccessFields, Audience, Role, SystemFields } from './records.ts';

export interface Actor {
  userId: string;
  role: Role;
}

/**
 * Audience inheritance. Child records (list items, photos in an album,
 * comments on an event...) do not have their own audience — they always
 * mirror their parent's, and the server keeps them in step.
 */
export type ParentSpec =
  { kind: 'fixed'; collection: string; field: string } | { kind: 'dynamic'; collectionField: string; idField: string; allowed: readonly string[] };

export interface CreateRuleContext {
  actor: Actor;
  data: Record<string, unknown>;
  parent?: (AccessFields & Record<string, unknown>) | undefined;
}

export interface CollectionDef<S extends z.ZodObject = z.ZodObject> {
  /** Permanent identifier: also the server table name and the sync protocol key. */
  name: string;
  module: ModuleId;
  label: { one: string; other: string };
  /** Data fields (system fields excluded). */
  schema: S;
  parent?: ParentSpec;
  defaultAudience: Audience;
  /**
   * What child members may do here:
   *  - read: view records whose audience includes them
   *  - contribute: also create records and work with items in shared containers
   */
  childAccess: 'read' | 'contribute';
  /**
   * collaborative: any adult who can see a record can edit it
   * author: only the author can edit (comments, memories, notices)
   */
  editPolicy: 'collaborative' | 'author';
  /** Fields that cannot change after creation (e.g. the parent pointer). */
  immutableFields: readonly string[];
  /**
   * Long free-text fields. If two people edit one concurrently, the server
   * keeps the existing value and returns a conflict for human review instead
   * of silently picking a winner.
   */
  manualMergeFields: readonly string[];
  /** Fields the server always sets to the acting user on create. */
  actorFields?: readonly string[];
  /** Extra creation rule, evaluated identically on client and server. */
  canCreate?: (ctx: CreateRuleContext) => boolean;
}

export type DataOf<C extends CollectionDef> = z.infer<C['schema']>;
export type RecordOf<C extends CollectionDef> = SystemFields & DataOf<C>;

export function defineCollection<S extends z.ZodObject>(def: CollectionDef<S>): CollectionDef<S> {
  return def;
}

/** Resolve the parent pointer of a child record, if the collection has one. */
export function parentPointer(def: CollectionDef, data: Record<string, unknown>): { collection: string; id: string } | null {
  if (!def.parent) return null;
  if (def.parent.kind === 'fixed') {
    const id = data[def.parent.field];
    return typeof id === 'string' ? { collection: def.parent.collection, id } : null;
  }
  const collection = data[def.parent.collectionField];
  const id = data[def.parent.idField];
  if (typeof collection !== 'string' || typeof id !== 'string') return null;
  if (!def.parent.allowed.includes(collection)) return null;
  return { collection, id };
}
